import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard, { WidgetSuccessState } from "./WidgetCard";

const LogMismatchWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({
    jobLogsWithoutMileage: [],
    mileageWithoutJobLogs: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState("jobLogs");

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  // Check if user is admin and viewing all data (not just their own)
  const showJobCoach = user?.is_admin && !onlyMyData;

  useEffect(() => {
    const fetchMismatches = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const response = await secureAxios.get("/api/widgets/log-mismatches", {
          params: { only_my_data: onlyMyData },
        });
        setData(
          response.data || {
            jobLogsWithoutMileage: [],
            mileageWithoutJobLogs: [],
          }
        );
        setError(false);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching log mismatches:", err);
        }
        setError(true);
        setData({ jobLogsWithoutMileage: [], mileageWithoutJobLogs: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchMismatches();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  const handleAddMileage = (jobLog) => {
    const formattedDate = jobLog.date_of_service
      ? jobLog.date_of_service.includes("T")
        ? jobLog.date_of_service.split("T")[0]
        : jobLog.date_of_service
      : new Date().toISOString().split("T")[0];

    const params = new URLSearchParams({
      consumer_id: jobLog.consumer_id,
      date: formattedDate,
      user_id: jobLog.user_id,
    });
    navigate(`/crm/mileage-logs?${params.toString()}`);
  };

  const handleAddJobLog = (mileageLog) => {
    const formattedDate = mileageLog.trip_date
      ? mileageLog.trip_date.includes("T")
        ? mileageLog.trip_date.split("T")[0]
        : mileageLog.trip_date
      : new Date().toISOString().split("T")[0];

    const params = new URLSearchParams({
      consumer_id: mileageLog.consumer_id,
      date: formattedDate,
      user_id: mileageLog.user_id,
    });
    navigate(`/crm/job-logs?${params.toString()}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    
    // If it's a plain date string (YYYY-MM-DD), parse it directly to avoid timezone shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split("-");
      const date = new Date(year, month - 1, day); // Local date, no timezone shift
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    
    // For ISO datetime strings, check if it's a midnight UTC (date-only value)
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();
    const isDateOnly = (hours === 0 || hours === 12) && minutes === 0 && seconds === 0;
    
    if (isDateOnly) {
      // Use UTC values to prevent timezone shift for date-only values
      return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
        .toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
    }
    
    // For actual datetime values, use local timezone
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const totalMismatches =
    (data.jobLogsWithoutMileage?.length || 0) +
    (data.mileageWithoutJobLogs?.length || 0);

  // Show success state when no mismatches
  if (!loading && !error && totalMismatches === 0) {
    return (
      <WidgetCard
        title="Log Sync Check"
        icon="bi-arrow-repeat"
        iconColor="success"
        loading={loading}
      >
        <WidgetSuccessState message="All job logs and mileage logs are properly matched!" />
      </WidgetCard>
    );
  }

  const subtitle = `Entries that may need a matching log${
    onlyMyData ? " (my logs only)" : ""
  }`;

  return (
    <WidgetCard
      title="Log Sync Check"
      subtitle={!loading && !error ? subtitle : undefined}
      icon="bi-arrow-repeat"
      iconColor="warning"
      loading={loading}
      error={error}
      errorMessage="Unable to load log mismatch data"
      badge={totalMismatches > 0 ? totalMismatches : undefined}
      badgeColor="warning"
    >
      {/* Tab navigation */}
      <ul className="nav nav-tabs nav-fill mb-3 widget-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "jobLogs" ? "active" : ""}`}
            onClick={() => setActiveTab("jobLogs")}
          >
            Job Logs
            {data.jobLogsWithoutMileage?.length > 0 && (
              <span className="badge bg-warning text-dark ms-2">
                {data.jobLogsWithoutMileage.length}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "mileage" ? "active" : ""}`}
            onClick={() => setActiveTab("mileage")}
          >
            Mileage
            {data.mileageWithoutJobLogs?.length > 0 && (
              <span className="badge bg-warning text-dark ms-2">
                {data.mileageWithoutJobLogs.length}
              </span>
            )}
          </button>
        </li>
      </ul>

      {/* Job Logs without Mileage tab */}
      {activeTab === "jobLogs" && (
        <>
          {data.jobLogsWithoutMileage?.length === 0 ? (
            <WidgetSuccessState message="All job logs have matching mileage entries" />
          ) : (
            <>
              {/* Desktop view */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered widget-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Consumer</th>
                        {showJobCoach && <th>Job Coach</th>}
                        <th>Hours</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.jobLogsWithoutMileage.slice(0, 10).map((log) => (
                        <tr key={log.job_log_id}>
                          <td>{formatDate(log.date_of_service)}</td>
                          <td className="widget-consumer-name">
                            {log.consumer_name}
                          </td>
                          {showJobCoach && (
                            <td className="widget-coach-name">
                              {log.job_coach_name || "Ã¢â‚¬â€"}
                            </td>
                          )}
                          <td>{log.hours_worked}h</td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleAddMileage(log)}
                              title="Add mileage for this job log"
                            >
                              <i className="bi bi-plus-circle me-1"></i>
                              Add Mileage
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile view */}
              <div className="d-block d-md-none">
                {data.jobLogsWithoutMileage.slice(0, 5).map((log) => (
                  <div
                    key={log.job_log_id}
                    className="widget-mobile-card widget-mobile-card--warning border bg-light"
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="fw-bold">{log.consumer_name}</div>
                        <div className="small text-muted">
                          {formatDate(log.date_of_service)} - {log.hours_worked}h
                        </div>
                        {showJobCoach && log.job_coach_name && (
                          <div className="small text-muted">
                            <i className="bi bi-person me-1"></i>
                            {log.job_coach_name}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={() => handleAddMileage(log)}
                        title="Add mileage"
                      >
                        <i className="bi bi-plus-circle"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {data.jobLogsWithoutMileage.length > 10 && (
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing 10 of {data.jobLogsWithoutMileage.length} job logs
                  </small>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Mileage without Job Logs tab */}
      {activeTab === "mileage" && (
        <>
          {data.mileageWithoutJobLogs?.length === 0 ? (
            <WidgetSuccessState message="All mileage logs have matching job log entries" />
          ) : (
            <>
              {/* Desktop view */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered widget-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Consumer</th>
                        {showJobCoach && <th>Job Coach</th>}
                        <th>Miles</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.mileageWithoutJobLogs.slice(0, 10).map((log) => (
                        <tr key={log.log_id}>
                          <td>{formatDate(log.trip_date)}</td>
                          <td className="widget-consumer-name">
                            {log.consumer_name}
                          </td>
                          {showJobCoach && (
                            <td className="widget-coach-name">
                              {log.job_coach_name || "Ã¢â‚¬â€"}
                            </td>
                          )}
                          <td>{log.total_miles} mi</td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleAddJobLog(log)}
                              title="Add job log for this mileage"
                            >
                              <i className="bi bi-plus-circle me-1"></i>
                              Add Job Log
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile view */}
              <div className="d-block d-md-none">
                {data.mileageWithoutJobLogs.slice(0, 5).map((log) => (
                  <div
                    key={log.log_id}
                    className="widget-mobile-card widget-mobile-card--warning border bg-light"
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="fw-bold">{log.consumer_name}</div>
                        <div className="small text-muted">
                          {formatDate(log.trip_date)} • {log.total_miles} mi
                        </div>
                        {showJobCoach && log.job_coach_name && (
                          <div className="small text-muted">
                            <i className="bi bi-person me-1"></i>
                            {log.job_coach_name}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={() => handleAddJobLog(log)}
                        title="Add job log"
                      >
                        <i className="bi bi-plus-circle"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {data.mileageWithoutJobLogs.length > 10 && (
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing 10 of {data.mileageWithoutJobLogs.length} mileage
                    logs
                  </small>
                </div>
              )}
            </>
          )}
        </>
      )}

      <div className="text-muted small mt-3">
        <i className="bi bi-info-circle me-1"></i>
        Tip: Billable service visits should have both a job log and mileage entry.
        Non-billable logs and services that don't require mileage are excluded.
      </div>
    </WidgetCard>
  );
};

export default LogMismatchWidget;